import { useState } from 'react';

/**
 * Selection for a pool's creators, held outside the table so it survives refetches. A row is one
 * creator, so the selection is keyed on the creator id rather than on a client influencer account.
 */
export function usePoolCreatorSelection(poolId: number | null | undefined) {
    const [selectedCreatorIds, setSelectedCreatorIds] = useState<Set<string>>(() => new Set());
    const [selectedPoolId, setSelectedPoolId] = useState(poolId);

    // Another pool lists other creators entirely, so its selection must not carry over. An effect
    // would clear it only after the new pool had painted once against the old selection.
    if (selectedPoolId !== poolId) {
        setSelectedPoolId(poolId);
        setSelectedCreatorIds(new Set());
    }

    const toggleCreator = (creatorId: string) => {
        setSelectedCreatorIds((previous) => {
            const next = new Set(previous);
            if (next.has(creatorId)) {
                next.delete(creatorId);
            } else {
                next.add(creatorId);
            }

            return next;
        });
    };

    const selectCreators = (creatorIds: string[]) => {
        setSelectedCreatorIds(new Set(creatorIds));
    };

    const clearSelection = () => {
        setSelectedCreatorIds(new Set());
    };

    return {
        selectedCreatorIds,
        toggleCreator,
        selectCreators,
        clearSelection,
    };
}
